import React, { memo } from "react";
import { Grid, Card, Box, Stack, Chip } from "@mui/material";
import { Bar, Line } from "react-chartjs-2";
import {
  Users,
  Activity,
  TrendingUp,
  Calendar,
  CreditCard,
  PieChart,
  Stethoscope,
  PlusCircle,
  UserPlus,
  FileText,
  Layers,
} from "lucide-react";
import MDTypography from "components/MDTypography";
import {
  StatCard,
  QuickAction,
  SectionLabel,
  WelcomeHeader,
} from "./DashboardComponents";

const MemoizedBarChart = memo(({ data, options }) => (
  <Bar data={data} options={options} />
));
const MemoizedLineChart = memo(({ data, options }) => (
  <Line data={data} options={options} />
));

const AdminDashboardView = ({
  User,
  currentTime,
  chartData,
  histogramData,
  lineChartOptions,
  baseChartOptions,
  translations,
  handleOnClick,
  highlights, // New prop from Dashboard.js
}) => {
  return (
    <Box>
      <WelcomeHeader User={User} showId currentTime={currentTime} />

      {/* ── Updated Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Sessions Today"
            value={highlights.sessionsToday}
            icon={Activity}
            color="var(--medco-blue)"
            trend="up"
            trendLabel="Live"
            isGradient
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Patients (Today)"
            value={highlights.totalPatients}
            icon={Users}
            trend="up"
            trendLabel="Live"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Sessions (Month)"
            value={highlights.sessionsThisMonth}
            icon={Calendar}
            color="var(--accent-purple)"
            trend="up"
            trendLabel="Current Month"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Users (Month)"
            value={highlights.totalUsers}
            icon={TrendingUp}
            color="var(--accent-green)"
            trend="up"
            trendLabel="Current Month"
          />
        </Grid>
      </Grid>

      {/* ── Charts ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Card className="chart-card">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box>
                <MDTypography className="chart-card__title">
                  Patient Flow — 24h
                </MDTypography>
                <MDTypography className="chart-card__subtitle">
                  New patients vs. sessions by hour
                </MDTypography>
              </Box>
              <Chip
                label="Live"
                size="small"
                className="chart-card__live-chip"
              />
            </Stack>
            <Box className="chart-card__body">
              <MemoizedLineChart data={chartData} options={lineChartOptions} />
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className="chart-card">
            <Box sx={{ mb: 2 }}>
              <MDTypography className="chart-card__title">
                Users Growth
              </MDTypography>
              <MDTypography className="chart-card__subtitle">
                New registrations per month
              </MDTypography>
            </Box>
            <Box className="chart-card__body">
              <MemoizedBarChart
                data={histogramData}
                options={baseChartOptions}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* ── Quick Actions ── */}
      <SectionLabel icon={Layers}>Quick Actions</SectionLabel>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.sidebarChildernAccountingAccountingSessions}
            subtitle="View and manage billing sessions"
            icon={CreditCard}
            color="var(--medco-blue)"
            onClick={() => handleOnClick("/Accountingsessions")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.DashBoardAppointments}
            subtitle="Schedule and track appointments"
            icon={Calendar}
            color="var(--accent-orange)"
            onClick={() => handleOnClick("/Appointments")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.DashBoardFeedbackStatistics}
            subtitle="Patient satisfaction reports"
            icon={PieChart}
            color="var(--accent-indigo)"
            onClick={() => handleOnClick("/Appointments/FeedbackStatistics")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.sidebarChildernAccountingCreateInPatientStay}
            subtitle="Register in-patient admissions"
            icon={Stethoscope}
            color="var(--accent-red)"
            onClick={() => handleOnClick("/CreateInPatientStay")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.DashBoardCreateMultiPackages}
            subtitle="Build and manage care packages"
            icon={PlusCircle}
            color="var(--accent-amber)"
            onClick={() => handleOnClick("/CreatePackage")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.DashBoardPatients}
            subtitle="Browse full patient history"
            icon={Users}
            color="var(--accent-green)"
            onClick={() => handleOnClick("/PatientHistory")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.DashboardCreateuser}
            subtitle="Add staff or patient accounts"
            icon={UserPlus}
            color="var(--accent-purple)"
            onClick={() => handleOnClick("/CreateUser")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={translations.DashboardCreatecontract}
            subtitle="Draft and sign agreements"
            icon={FileText}
            color="var(--accent-cyan)"
            onClick={() => handleOnClick("/CreateContract")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickAction
            title={
              translations.sidebarChildernServicesViewServiceBundles ||
              "Service Bundles"
            }
            subtitle="View and configure service bundles"
            icon={Layers}
            color="var(--accent-pink)"
            badge="New"
            onClick={() => handleOnClick("/ViewServiceBundles")}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboardView;
